import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Calendar as CalendarIcon, DollarSign, Users, Activity, TrendingUp } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ---- helpers ----
const toISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const fromISO = (s) => new Date(`${s}T12:00:00`);
const fmtBR = (iso) => {
  if (!iso) return '—';
  const d = fromISO(String(iso).slice(0, 10));
  return d.toLocaleDateString('pt-BR');
};
const fmtMoney = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtShortDate = (iso) => {
  const d = fromISO(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// ---- preset periods ----
const buildPresets = () => {
  const today = new Date();
  const todayISO = toISO(today);
  const firstOfMonth = toISO(new Date(today.getFullYear(), today.getMonth(), 1));
  const ago7 = new Date(today); ago7.setDate(today.getDate() - 6);
  const ago30 = new Date(today); ago30.setDate(today.getDate() - 29);
  const firstPrevMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastPrevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
  const firstOfYear = new Date(today.getFullYear(), 0, 1);
  return {
    today: { start: todayISO, end: todayISO, label: 'Hoje' },
    last7: { start: toISO(ago7), end: todayISO, label: '7 dias' },
    last30: { start: toISO(ago30), end: todayISO, label: '30 dias' },
    thisMonth: { start: firstOfMonth, end: todayISO, label: 'Este mês' },
    lastMonth: { start: toISO(firstPrevMonth), end: toISO(lastPrevMonth), label: 'Mês passado' },
    thisYear: { start: toISO(firstOfYear), end: todayISO, label: 'Este ano' },
  };
};

export const DashboardHome = () => {
  const presets = useMemo(buildPresets, []);
  const [startDate, setStartDate] = useState(presets.thisMonth.start);
  const [endDate, setEndDate] = useState(presets.thisMonth.end);
  const [appliedStart, setAppliedStart] = useState(presets.thisMonth.start);
  const [appliedEnd, setAppliedEnd] = useState(presets.thisMonth.end);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchStats = useCallback(async (s, e) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/stats/dashboard`, {
        params: { start_date: s, end_date: e },
        withCredentials: true,
      });
      setStats(data);
    } catch (err) {
      console.error('Stats error:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchStats(appliedStart, appliedEnd);
  }, [fetchStats, appliedStart, appliedEnd]);

  const applyFilter = () => {
    if (!startDate || !endDate) return;
    if (startDate > endDate) return;
    setAppliedStart(startDate);
    setAppliedEnd(endDate);
  };

  const clearFilter = () => {
    setStartDate(presets.thisMonth.start);
    setEndDate(presets.thisMonth.end);
    setAppliedStart(presets.thisMonth.start);
    setAppliedEnd(presets.thisMonth.end);
  };

  const applyPreset = (preset) => {
    setStartDate(preset.start);
    setEndDate(preset.end);
    setAppliedStart(preset.start);
    setAppliedEnd(preset.end);
  };

  return (
    <div className="space-y-6" data-testid="dashboard-home">
      {/* HEADER */}
      <div>
        <h2 className="text-3xl font-medium text-[#0F1B3D] dark:text-[#FAF6EE] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Bem-vinda, Dra. Fernanda
        </h2>
        <p className="text-[#5F6B85] dark:text-[#FAF6EE]/70">Visão geral da sua clínica</p>
      </div>

      {/* PERIOD FILTER */}
      <Card className="border-[#E5DCC8] dark:border-[#1A2952]" data-testid="dashboard-period-filter">
        <CardContent className="p-5 space-y-3">
          <div className="flex flex-col md:flex-row md:items-end gap-3">
            <div className="flex items-center gap-2 mr-1 text-[#0F1B3D] dark:text-[#FAF6EE]">
              <CalendarIcon className="h-4 w-4" />
              <span className="text-sm font-medium">Período</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 flex-1">
              <div className="flex-1 min-w-[140px]">
                <label className="text-xs text-[#7A85A0]">Data inicial</label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="filter-start-date"
                />
              </div>
              <div className="flex-1 min-w-[140px]">
                <label className="text-xs text-[#7A85A0]">Data final</label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="filter-end-date"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={applyFilter}
                className="bg-[#A8174E] hover:bg-[#8E1142] text-white"
                data-testid="filter-apply-btn"
              >
                Aplicar
              </Button>
              <Button
                onClick={clearFilter}
                variant="outline"
                className="border-[#E5DCC8] dark:border-[#1A2952]"
                data-testid="filter-clear-btn"
              >
                Limpar
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-2 border-t border-[#FAF6EE] dark:border-[#1A2952]">
            {Object.entries(presets).map(([key, p]) => {
              const isActive = appliedStart === p.start && appliedEnd === p.end;
              return (
                <button
                  key={key}
                  onClick={() => applyPreset(p)}
                  className={`px-3 py-1 text-xs rounded-full border transition-all ${
                    isActive
                      ? 'bg-[#0F1B3D] text-white border-[#0F1B3D]'
                      : 'border-[#E5DCC8] dark:border-[#1A2952] text-[#5F6B85] dark:text-[#FAF6EE]/80 hover:bg-[#FAF6EE] dark:hover:bg-[#1A2952]'
                  }`}
                  data-testid={`preset-${key}`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>

          <div className="text-sm text-[#5F6B85] dark:text-[#FAF6EE]/70 pt-1">
            <span className="font-medium text-[#0F1B3D] dark:text-[#FAF6EE]">Período selecionado:</span>{' '}
            <span data-testid="period-display">{fmtBR(appliedStart)} até {fmtBR(appliedEnd)}</span>
          </div>
        </CardContent>
      </Card>

      {loading && <p className="text-sm text-[#7A85A0]">Carregando...</p>}

      {stats && (
        <>
          {/* TOP CARDS — Pacientes / Consultas / Pagamentos */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-[#E5DCC8] dark:border-[#1A2952]" data-testid="stat-card-patients">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardDescription>Pacientes</CardDescription>
                  <Users className="h-4 w-4 text-[#0F1B3D] dark:text-[#FAF6EE]/70" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold text-[#0F1B3D] dark:text-[#FAF6EE]">{stats.total_patients}</div>
                <p className="text-xs text-[#7A85A0] mt-1">cadastrados no período</p>
                <div className="flex gap-3 mt-3 text-xs">
                  <span className="text-[#0F1B3D] dark:text-[#FAF6EE]">{stats.active_patients} ativos</span>
                  <span className="text-[#A8174E]">{stats.cancelled_patients} cancelados</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#E5DCC8] dark:border-[#1A2952]" data-testid="stat-card-appointments">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardDescription>Consultas</CardDescription>
                  <Activity className="h-4 w-4 text-[#0F1B3D] dark:text-[#FAF6EE]/70" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold text-[#0F1B3D] dark:text-[#FAF6EE]">{stats.total_appointments}</div>
                <p className="text-xs text-[#7A85A0] mt-1">{stats.completed_appointments} realizadas</p>
                <div className="flex gap-3 mt-3 text-xs flex-wrap">
                  <span className="text-[#5F6B85] dark:text-[#FAF6EE]/80">{stats.scheduled_appointments} agendadas</span>
                  <span className="text-[#A8174E]">{stats.cancelled_appointments} canceladas</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#E5DCC8] dark:border-[#1A2952]" data-testid="stat-card-payments">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardDescription>Pagamentos</CardDescription>
                  <DollarSign className="h-4 w-4 text-[#0F1B3D] dark:text-[#FAF6EE]/70" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold text-[#0F1B3D] dark:text-[#FAF6EE]">{fmtMoney(stats.total_revenue)}</div>
                <p className="text-xs text-[#7A85A0] mt-1">{stats.payments_count} recebidos · ticket {fmtMoney(stats.avg_ticket)}</p>
                <p className="text-xs text-[#A8174E] mt-1">{fmtMoney(stats.pending_revenue)} pendente</p>
              </CardContent>
            </Card>
          </div>

          {/* REVENUE BY TYPE */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border-[#E5DCC8] dark:border-[#1A2952] border-l-4 border-l-[#0F1B3D]" data-testid="revenue-clinica-geral">
              <CardContent className="p-5">
                <div className="text-xs font-semibold text-[#0F1B3D] dark:text-[#FAF6EE]/80 uppercase tracking-wide mb-1">Clínica Geral</div>
                <div className="text-3xl font-bold text-[#0F1B3D] dark:text-[#FAF6EE]">{fmtMoney(stats.revenue_by_type?.clinica_geral || 0)}</div>
                <p className="text-xs text-[#7A85A0] mt-1">{stats.appointments_by_type?.clinica_geral || 0} consulta(s)</p>
              </CardContent>
            </Card>
            <Card className="border-[#E5DCC8] dark:border-[#1A2952] border-l-4 border-l-[#B8845C]" data-testid="revenue-tirzepatida">
              <CardContent className="p-5">
                <div className="text-xs font-semibold text-[#B8845C] uppercase tracking-wide mb-1">Tirzepatida</div>
                <div className="text-3xl font-bold text-[#B8845C]">{fmtMoney(stats.revenue_by_type?.tirzepatida || 0)}</div>
                <p className="text-xs text-[#7A85A0] mt-1">{stats.appointments_by_type?.tirzepatida || 0} consulta(s)</p>
              </CardContent>
            </Card>
          </div>

          {/* PATIENT TYPE BREAKDOWN */}
          <Card className="border-[#E5DCC8] dark:border-[#1A2952]" data-testid="patient-breakdown">
            <CardHeader className="pb-2"><CardDescription>Pacientes por tipo (cadastrados no período)</CardDescription></CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-[#0F1B3D] dark:text-[#FAF6EE]">Clínica Geral</span>
                  <span className="text-2xl font-bold text-[#0F1B3D] dark:text-[#FAF6EE]">{stats.patients_by_type?.clinica_geral?.total || 0}</span>
                </div>
                <div className="flex gap-3 mt-2 text-xs">
                  <span className="text-[#0F1B3D] dark:text-[#FAF6EE]/80">{stats.patients_by_type?.clinica_geral?.active || 0} ativos</span>
                  <span className="text-[#A8174E]">{stats.patients_by_type?.clinica_geral?.cancelled || 0} canc.</span>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-[#B8845C]">Tirzepatida</span>
                  <span className="text-2xl font-bold text-[#B8845C]">{stats.patients_by_type?.tirzepatida?.total || 0}</span>
                </div>
                <div className="flex gap-3 mt-2 text-xs">
                  <span className="text-[#0F1B3D] dark:text-[#FAF6EE]/80">{stats.patients_by_type?.tirzepatida?.active || 0} ativos</span>
                  <span className="text-[#A8174E]">{stats.patients_by_type?.tirzepatida?.cancelled || 0} canc.</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* REVENUE BY DAY CHART */}
          <Card className="border-[#E5DCC8] dark:border-[#1A2952]" data-testid="revenue-chart">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-[#0F1B3D] dark:text-[#FAF6EE]/70" />
                <CardDescription>Evolução do faturamento por dia</CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              {stats.revenue_by_day && stats.revenue_by_day.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={stats.revenue_by_day} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5DCC8" />
                      <XAxis dataKey="date" tickFormatter={fmtShortDate} stroke="#5F6B85" fontSize={11} />
                      <YAxis tickFormatter={(v) => `R$${v}`} stroke="#5F6B85" fontSize={11} />
                      <Tooltip
                        labelFormatter={(label) => fmtBR(label)}
                        formatter={(v) => [fmtMoney(v), 'Recebido']}
                        contentStyle={{ background: '#FAF6EE', border: '1px solid #E5DCC8', borderRadius: 8 }}
                      />
                      <Line type="monotone" dataKey="amount" stroke="#A8174E" strokeWidth={2} dot={{ r: 3, fill: '#A8174E' }} activeDot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-sm text-[#7A85A0] text-center py-8">Sem dados no período</p>
              )}
            </CardContent>
          </Card>

          {/* RECENT PAYMENTS */}
          <Card className="border-[#E5DCC8] dark:border-[#1A2952]" data-testid="recent-payments">
            <CardHeader className="pb-2"><CardDescription>Últimos pagamentos recebidos</CardDescription></CardHeader>
            <CardContent>
              {stats.recent_payments && stats.recent_payments.length > 0 ? (
                <div className="divide-y divide-[#FAF6EE] dark:divide-[#1A2952]">
                  {stats.recent_payments.map((p) => (
                    <div key={p.payment_id} className="flex items-center justify-between py-3 text-sm" data-testid={`recent-payment-${p.payment_id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[#0F1B3D] dark:text-[#FAF6EE] truncate">{p.patient_name}</div>
                        <div className="text-xs text-[#7A85A0]">{p.type === 'tirzepatida' ? 'Tirzepatida' : 'Clínica Geral'} · {fmtBR(p.paid_at)}</div>
                      </div>
                      <div className="text-base font-semibold text-[#0F1B3D] dark:text-[#FAF6EE] tabular-nums">{fmtMoney(p.amount)}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[#7A85A0] text-center py-6">Nenhum pagamento recebido no período</p>
              )}
            </CardContent>
          </Card>

          {/* keep below filter */}
        </>
      )}
    </div>
  );
};

// also export default for compatibility
export default DashboardHome;
